"use client";

import React, { useState, useEffect } from "react";
import { ClipboardCheck, Search, ShieldCheck, Truck } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Popover, PopoverAnchor, PopoverContent } from "@/components/ui/popover";
const PROCESS_STEPS = [{
  id: "01",
  title: "Commercial Quote",
  shortDesc: "Wholesale pricing",
  detail: "We provide competitive commercial-grade pricing for bulk orders of cement, steel, and electrical hardware tailored to your specific project volume and timeline.",
  icon: ClipboardCheck
}, {
  id: "02",
  title: "Bulk Allocation",
  shortDesc: "Inventory sourcing",
  detail: "Our logistics team allocates high-grade materials from our vetted network, ensuring all supplies meet the rigorous demands of Australian building standards.",
  icon: Search
}, {
  id: "03",
  title: "Quality Audit",
  shortDesc: "Compliance check",
  detail: "Every shipment undergoes a comprehensive quality audit to verify structural integrity and full compliance with Australian construction regulations and ACN 669 488 386.",
  icon: ShieldCheck
}, {
  id: "04",
  title: "National Dispatch",
  shortDesc: "Reliable logistics",
  detail: "Coordinated delivery across Australia, from our Victoria hub to your site, utilizing a streamlined transport network to minimize downtime for construction crews.",
  icon: Truck
}];
export default function SupplyChainProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const [progressValue, setProgressValue] = useState(0);
  const [activeStep, setActiveStep] = useState<number | null>(null);
  useEffect(() => {
    if (isVisible) {
      const duration = 1500;
      const startTime = performance.now();
      const animateProgress = (currentTime: number) => {
        const elapsed = currentTime - startTime;
        const progress = Math.min(elapsed / duration * 100, 100);
        setProgressValue(progress);
        if (progress < 100) {
          requestAnimationFrame(animateProgress);
        }
      };
      requestAnimationFrame(animateProgress);
    }
  }, [isVisible]);
  return <section data-section-id="140410" id="distribution-process" className="dark bg-background py-24 relative overflow-hidden">
      {}
      <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />

      <div className="container mx-auto px-4 relative z-10">
        <div className="max-w-3xl mb-16">
          <h2 className="text-3xl md:text-5xl font-bold tracking-tighter text-foreground mb-4 uppercase font-serif">
            Wholesale Logistics
          </h2>
          <p className="text-lg text-muted-foreground font-sans">
            From initial commercial inquiry to national site dispatch, Brendon J Building Material Wholesalers 
            ensures a seamless supply chain for Australia's construction industry.
          </p>
        </div>

        <div ref={ref} className="relative mt-12 md:mt-24">
          {}
          <div className="hidden md:block absolute top-1/2 left-0 w-full -translate-y-1/2 px-[12.5%] z-0">
            <div className="relative h-1 w-full bg-secondary/20 rounded-none">
              <Progress value={progressValue} className="h-full rounded-none bg-transparent [&>div]:bg-primary" />
            </div>
          </div>

          {}
          <div className="md:hidden absolute top-0 left-8 bottom-0 w-1 bg-secondary/20 z-0">
            <div className="w-full bg-primary transition-all duration-100 ease-out" style={{
            height: `${progressValue}%`
          }} />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 md:gap-4 relative z-10">
            {PROCESS_STEPS.map((step, i) => {
            const Icon = step.icon;
            return <div key={step.id} data-index={i} className="relative flex md:block items-center gap-6 md:gap-0" onMouseEnter={() => setActiveStep(i)} onMouseLeave={() => setActiveStep(null)} onFocus={() => setActiveStep(i)} onBlur={() => setActiveStep(null)} tabIndex={0}>
                  {}
                  <div className="md:hidden relative z-10 flex-shrink-0 w-16 h-16 rounded-sm border-2 border-border bg-card flex items-center justify-center">
                    <span className="font-mono text-xl font-bold text-primary">
                      {step.id}
                    </span>
                  </div>

                  <Popover open={activeStep === i}>
                    <PopoverAnchor asChild>
                      <div className="w-full outline-none">
                        <Card className="border-2 border-border rounded-sm bg-card hover:border-primary transition-colors duration-300 cursor-default shadow-sm hover:shadow-lg relative overflow-hidden group">
                          {}
                          <div className="absolute top-0 left-0 w-full h-1 bg-secondary group-hover:bg-primary transition-colors duration-300" />
                          
                          <CardHeader className="pb-4 pt-6">
                            <div className="flex justify-between items-start mb-4">
                              <Icon className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors" />
                              <span className="hidden md:block font-mono text-3xl font-bold text-secondary/20 group-hover:text-primary/20 transition-colors">
                                {step.id}
                              </span>
                            </div>
                            <CardTitle className="text-lg font-bold uppercase tracking-tight font-serif">
                              {step.title}
                            </CardTitle>
                            <CardDescription className="text-sm font-medium text-muted-foreground font-sans">
                              {step.shortDesc}
                            </CardDescription>
                          </CardHeader>
                        </Card>
                      </div>
                    </PopoverAnchor>
                    <PopoverContent side="top" align="center" sideOffset={16} className="w-80 border-2 border-border rounded-sm shadow-2xl bg-popover p-6 animate-in fade-in zoom-in-95 duration-200" onMouseEnter={() => setActiveStep(i)} onMouseLeave={() => setActiveStep(null)}>
                      <div className="space-y-3">
                        <div className="flex items-center gap-3 border-b border-border pb-3">
                          <span className="font-mono text-primary font-bold">
                            {step.id}
                          </span>
                          <h4 className="font-bold uppercase tracking-tight text-popover-foreground font-serif">
                            {step.title}
                          </h4>
                        </div>
                        <p className="text-sm text-muted-foreground leading-relaxed font-sans">
                          {step.detail}
                        </p>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}